import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import "./MancatoLogin.css";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import Lf23Loader from "../components/Lf23Loader";

const MancatoLogin = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [dati, setDati] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [limit, setLimit] = useState(20);

    // UI State
    const [viewMode, setViewMode] = useState("table");
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreen]);
    const [showFilters, setShowFilters] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedRows, setExpandedRows] = useState(new Set());

    const [sortConfig, setSortConfig] = useState({ key: 'data_inizio_percorso', direction: 'desc' });

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
        setPage(1);
    };

    // Stats globali
    const [stats, setStats] = useState({
        totalVeicoli: 0,
        erroriLogin: 0,
        loginOk: 0
    });

    // Filtri
    const [targa, setTarga] = useState("");
    const [driver, setDriver] = useState("");
    const [loginStatus, setLoginStatus] = useState("false"); // Default: Mancato Login

    const loginOptions = [
        { value: "TUTTI", label: "Tutti" },
        { value: "true", label: "Login" },
        { value: "false", label: "Mancato Login" }
    ];

    const fetchStats = async () => {
        try {
            // Fetch tutti i dati per calcolare le statistiche globali
            const res = await axios.get(`${API_URL}/webfleet/mancato-login`, {
                params: {
                    page: 1,
                    limit: 999999, // Prendi tutti i dati per le statistiche
                    targa,
                    driver,
                    loginStatus
                }
            });

            const allData = res.data.data || [];

            // Calcola statistiche globali
            const totalVeicoli = res.data.total || 0;
            const erroriLogin = allData.filter(r => !r.logged).length;
            const loginOk = allData.filter(r => r.logged).length;

            setStats({
                totalVeicoli,
                erroriLogin,
                loginOk
            });
        } catch (err) {
            console.error("Errore nel caricamento statistiche:", err);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/webfleet/mancato-login`, {
                params: {
                    page,
                    limit,
                    targa,
                    driver,
                    loginStatus,
                    sortBy: sortConfig.key || undefined,
                    sortDir: sortConfig.direction || undefined
                }
            });
            setDati(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            console.error("Errore nel caricamento dati:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchStats();
        // eslint-disable-next-line
    }, [page, limit, sortConfig]);


    const handleSubmit = (e) => {
        e.preventDefault();
        setPage(1);
        fetchData();
        fetchStats();
    };

    const handleReset = () => {
        setTarga("");
        setDriver("");
        setLoginStatus("false"); // Default: Mancato Login
        setSearchTerm("");
        setPage(1);
        fetchData();
        fetchStats();
    };

    const toggleRowExpansion = (idx) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(idx)) {
            newExpanded.delete(idx);
        } else {
            newExpanded.add(idx);
        }
        setExpandedRows(newExpanded);
    };

    const totalPages = Math.ceil(total / limit);

    const filteredDati = dati.filter(row => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            row.targa?.toLowerCase().includes(search) ||
            row.driver?.toLowerCase().includes(search) ||
            row.ultimo_driver?.toLowerCase().includes(search) ||
            row.posizione_inizio?.toLowerCase().includes(search) ||
            row.ultima_posizione?.toLowerCase().includes(search)
        );
    });

    const formatGap = (gap) => {
        if (!gap || gap === "NaT" || gap === "00:00:00") return "-";

        // Formato dal DB: "X days HH:MM:SS" — estrai giorni e tempo separatamente
        let days = 0;
        let timePart = gap;
        if (gap.includes("days")) {
            const [daysPart, rest] = gap.split(" days ");
            days = parseInt(daysPart, 10) || 0;
            timePart = rest || "00:00:00";
        }

        const parts = timePart.split(":");
        if (parts.length !== 3) return gap;

        const [h, m, s] = parts.map(p => parseInt(p, 10));
        const totalHours = days * 24 + h;
        const d = Math.floor(totalHours / 24);
        const hh = totalHours % 24;

        const result = [];
        if (d) result.push(`${d} giorno${d > 1 ? 'i' : ''}`);
        if (hh) result.push(`${hh}h`);
        if (m) result.push(`${m}m`);
        if (s) result.push(`${s}s`);

        return result.length ? result.join(" ") : "0s";
    };

    return (
        <div className="mancato-login-container">
            <header className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate('/produttivita')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Mancato Login</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfo(!showInfo)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">Monitoraggio errori di autenticazione driver</p>
                    </div>
                </div>
            </header>

            {/* Modal Informazioni */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                    <path d="M10 8h6"></path>
                                    <path d="M10 12h6"></path>
                                    <path d="M10 16h4"></path>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="modal-close" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    In questa pagina puoi monitorare, per ogni targa che ha effettuato spostamenti negli ultimi 2 giorni,
                                    il driver loggato. Le righe evidenziano eventuali <strong>errori di login</strong> e ne specificano la causa:
                                    <strong> "Mancato login"</strong> o <strong>"Mancato login dopo Power disconnected, controllare ultimo driver se disponibile"</strong>.
                                </p>
                            </div>
                            <div className="modal-section">
                                <h4>Come interpretare i dati</h4>
                                <ul>
                                    <li><strong>Gap</strong> - Indica il tempo trascorso tra l'inizio del percorso e l'ultima posizione registrata</li>
                                    <li><strong>Logged</strong> - Mostra lo stato del login o il tipo di errore rilevato</li>
                                    <li><strong>Ultimo Driver</strong> - Se disponibile, indica l'ultimo driver registrato prima della disconnessione</li>
                                </ul>
                                <p className="modal-tip">
                                    💡 Utilizza i filtri per targa, driver o stato del login per individuare rapidamente le anomalie.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-area">
                {/* Top Controls */}
                <div className="top-controls">
                    <div className="controls-left">
                        <button
                            className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                            </svg>
                            {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                        </button>

                        <div className="search-bar-compact">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input
                                type="text"
                                placeholder="Cerca..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="controls-right">
                        <div className="view-toggle-compact">
                            <button
                                className={`view-btn-compact ${viewMode === 'table' ? 'active' : ''}`}
                                onClick={() => setViewMode('table')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 9h18M3 15h18M9 3v18"></path>
                                </svg>
                                Tabella
                            </button>
                            <button
                                className={`view-btn-compact ${viewMode === 'cards' ? 'active' : ''}`}
                                onClick={() => setViewMode('cards')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="14" width="7" height="7"></rect>
                                    <rect x="3" y="14" width="7" height="7"></rect>
                                </svg>
                                Cards
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="filters-panel">
                        <form className="filters-form" onSubmit={handleSubmit}>
                            <div className="filters-grid">
                                <div className="filter-item">
                                    <label>Targa</label>
                                    <input
                                        type="text"
                                        placeholder="Targa..."
                                        value={targa}
                                        onChange={(e) => setTarga(e.target.value.trim())}
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Driver</label>
                                    <input
                                        type="text"
                                        placeholder="Driver..."
                                        value={driver}
                                        onChange={(e) => setDriver(e.target.value.trim())}
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Stato Login</label>
                                    <Select
                                        options={loginOptions}
                                        value={loginOptions.find(o => o.value === loginStatus)}
                                        onChange={(e) => setLoginStatus(e.value)}
                                        classNamePrefix="rs"
                                    />
                                </div>

                                <div className="filter-item filter-actions">
                                    <label>&nbsp;</label>
                                    <div className="filter-buttons">
                                        <button type="submit" className="btn-apply-compact">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            Applica
                                        </button>
                                        <button type="button" className="btn-reset-compact" onClick={handleReset}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                                <path d="M3 3v5h5"></path>
                                            </svg>
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* Stats Cards */}
                <div className="stats-grid-compact">
                    <div className="stat-card-compact stat-primary">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="8.5" cy="7" r="4"></circle>
                                <polyline points="17 11 19 13 23 9"></polyline>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Totale Veicoli</p>
                            <p className="stat-value-compact">{stats.totalVeicoli}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-danger">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Errori Login</p>
                            <p className="stat-value-compact">{stats.erroriLogin}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-success">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Login OK</p>
                            <p className="stat-value-compact">{stats.loginOk}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-info">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Periodo</p>
                            <p className="stat-value-compact">2 giorni</p>
                        </div>
                    </div>
                </div>

                {/* Results Info */}
                <div className="results-info">
                    <span>Mostrando <strong>{filteredDati.length}</strong> di <strong>{total}</strong> risultati</span>
                </div>

                {loading ? (
                    <Lf23Loader />
                ) : (
                    <>
                        {/* Table View */}
                        {viewMode === 'table' && (
                            <>
                            <div className="fullscreen-btn-row">
                                <button
                                    className="fullscreen-btn-compact"
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    title={isFullscreen ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreen ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <div className={`table-container-compact${isFullscreen ? ' fullscreen-table' : ''}`}>
                                <RotateDevice active={isFullscreen} />
                                {isFullscreen && (
                                    <div className="fullscreen-exit-bar">
                                        <button
                                            className="fullscreen-btn-compact"
                                            onClick={() => setIsFullscreen(false)}
                                            title="Esci da tutto schermo"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </svg>
                                        </button>
                                    </div>
                                )}
                                <div className="table-scroll-area">
                                <table className="modern-table-compact">
                                    <thead>
                                        <tr>
                                            <th style={{width: '30px'}}></th>
                                            {[
                                                { label: 'Targa', key: 'targa' },
                                                { label: 'Driver', key: 'driver' },
                                                { label: 'Ultimo Driver', key: 'ultimo_driver' },
                                                { label: 'Inizio Percorso', key: 'data_inizio_percorso' },
                                                { label: 'Gap', key: 'gap' },
                                                { label: 'Stato', key: 'logged' },
                                            ].map(({ label, key }) => (
                                                <th key={key} className="mancato-login-th-sortable" onClick={() => handleSort(key)}>
                                                    {label}
                                                    <span className="mancato-login-sort-icon">
                                                        {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredDati.map((row, idx) => (
                                            <React.Fragment key={idx}>
                                                <tr className="table-row-compact" onClick={() => toggleRowExpansion(idx)}>
                                                    <td className="expand-cell-compact">
                                                        <button className="expand-btn-compact">
                                                            {expandedRows.has(idx) ? (
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M18 15l-6-6-6 6"/>
                                                                </svg>
                                                            ) : (
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M6 9l6 6 6-6"/>
                                                                </svg>
                                                            )}
                                                        </button>
                                                    </td>
                                                    <td><span className="badge-compact badge-targa">{row.targa}</span></td>
                                                    <td>{row.driver || "-"}</td>
                                                    <td>{row.ultimo_driver || "-"}</td>
                                                    <td>{new Date(row.data_inizio_percorso).toLocaleString('it-IT', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}</td>
                                                    <td><span className="badge-compact badge-gap">{formatGap(row.gap)}</span></td>
                                                    <td>
                                                        {row.logged ? (
                                                            <span className="badge-compact badge-success">Login</span>
                                                        ) : (
                                                            <span className="badge-compact badge-alert">{row.tipo_errore}</span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {expandedRows.has(idx) && (
                                                    <tr className="expanded-row-compact">
                                                        <td colSpan="7">
                                                            <div className="expanded-content-compact">
                                                                <div className="expanded-grid-compact">
                                                                    <div className="expanded-item-compact">
                                                                        <span className="expanded-label-compact">Targa:</span>
                                                                        <span className="expanded-value-compact">{row.targa}</span>
                                                                    </div>
                                                                    <div className="expanded-item-compact">
                                                                        <span className="expanded-label-compact">Driver:</span>
                                                                        <span className="expanded-value-compact">{row.driver || "-"}</span>
                                                                    </div>
                                                                    <div className="expanded-item-compact">
                                                                        <span className="expanded-label-compact">Ultimo Driver:</span>
                                                                        <span className="expanded-value-compact">{row.ultimo_driver || "-"}</span>
                                                                    </div>
                                                                    <div className="expanded-item-compact">
                                                                        <span className="expanded-label-compact">Posizione Inizio:</span>
                                                                        <span className="expanded-value-compact">{row.posizione_inizio || "-"}</span>
                                                                    </div>
                                                                    <div className="expanded-item-compact">
                                                                        <span className="expanded-label-compact">Ultima Posizione:</span>
                                                                        <span className="expanded-value-compact">{row.ultima_posizione || "-"}</span>
                                                                    </div>
                                                                    <div className="expanded-item-compact">
                                                                        <span className="expanded-label-compact">Gap:</span>
                                                                        <span className="expanded-value-compact">{formatGap(row.gap)}</span>
                                                                    </div>
                                                                    <div className="expanded-item-compact">
                                                                        <span className="expanded-label-compact">Stato:</span>
                                                                        <span className="expanded-value-compact">{row.logged ? "Login OK" : row.tipo_errore}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                                </div>

                                {/* Paginazione dentro table-container per fullscreen */}
                                <div className="pagination-compact">
                                    <button
                                        className="pagination-btn-compact"
                                        disabled={page === 1}
                                        onClick={() => setPage(page - 1)}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M15 18l-6-6 6-6" />
                                        </svg>
                                        Prec
                                    </button>
                                    <div className="pagination-info-compact">
                                        <span className="pagination-text-compact">
                                            Pag <strong>{page}</strong> di <strong>{totalPages || 1}</strong>
                                        </span>
                                        <span className="pagination-total-compact">({total} risultati totali)</span>
                                    </div>
                                    <button
                                        className="pagination-btn-compact"
                                        disabled={page === totalPages || totalPages === 0}
                                        onClick={() => setPage(page + 1)}
                                    >
                                        Succ
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M9 18l6-6-6-6" />
                                        </svg>
                                    </button>
                                    <div className="items-per-page-compact">
                                        <span className="items-label-compact">Elementi per pagina:</span>
                                        <div className="items-buttons-compact">
                                            {[10, 20, 50, 100].map((num) => (
                                                <button
                                                    key={num}
                                                    className={`items-btn-compact ${limit === num ? "active" : ""}`}
                                                    onClick={() => {
                                                        setLimit(num);
                                                        setPage(1);
                                                    }}
                                                >
                                                    {num}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </>
                        )}

                        {/* Cards View */}
                        {viewMode === 'cards' && (
                            <div className="cards-grid-compact">
                                {filteredDati.map((row, idx) => (
                                    <div className="data-card-compact" key={idx}>
                                        <div className="card-header-compact">
                                            <span className="card-badge-compact">{row.targa}</span>
                                            {row.logged ? (
                                                <span className="card-status-success">Login OK</span>
                                            ) : (
                                                <span className="card-status-error">Errore</span>
                                            )}
                                        </div>
                                        <div className="card-body-compact">
                                            <div className="card-row-compact">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="12" cy="7" r="4"></circle>
                                                </svg>
                                                <span><strong>Driver:</strong> {row.driver || "-"}</span>
                                            </div>
                                            <div className="card-row-compact">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="8.5" cy="7" r="4"></circle>
                                                </svg>
                                                <span><strong>Ultimo Driver:</strong> {row.ultimo_driver || "-"}</span>
                                            </div>
                                            <div className="card-row-compact">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                                </svg>
                                                <span><strong>Inizio:</strong> {new Date(row.data_inizio_percorso).toLocaleString('it-IT')}</span>
                                            </div>
                                            <div className="card-divider-compact"></div>
                                            <div className="card-row-compact">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M12 2v20M2 12h20"></path>
                                                </svg>
                                                <span><strong>Posizione Inizio:</strong> {row.posizione_inizio || "-"}</span>
                                            </div>
                                            <div className="card-row-compact">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                                    <circle cx="12" cy="10" r="3"></circle>
                                                </svg>
                                                <span><strong>Ultima Posizione:</strong> {row.ultima_posizione || "-"}</span>
                                            </div>
                                            <div className="card-divider-compact"></div>
                                            <div className="card-row-compact">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <polyline points="12 6 12 12 16 14"></polyline>
                                                </svg>
                                                <span><strong>Gap:</strong> {formatGap(row.gap)}</span>
                                            </div>
                                            {!row.logged && (
                                                <div className="card-error-box">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="12" r="10"></circle>
                                                        <line x1="12" y1="8" x2="12" y2="12"></line>
                                                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                                    </svg>
                                                    <span>{row.tipo_errore}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                    </>
                )}
            </div>
        </div>
    );
};

export default MancatoLogin;
